import { For, Show, createEffect } from 'solid-js';
import { customShadowlessElement, type CorrectComponentType } from '@brytdesigns/web-component-core/utils';
import { correctElementType } from '@brytdesigns/web-component-core';

import { formatMoney, useCartRecommendations, type RecommendedProduct } from '@/lib/cart-recommendations';

type Props = {
  dataUrl: string;
  dataCartUrl: string;
  dataLimit: number;
  dataIntent: string;
  dataTitle: string;
};

const SLIDER_CONFIG = {
  mode: 'free-snap',
  loop: false,
  slides: { perView: 1.2, spacing: 16 },
  breakpoints: {
    '(min-width: 640px)': { slides: { perView: 2, spacing: 16 } },
    '(min-width: 1024px)': { slides: { perView: 3, spacing: 16 } },
  },
};

const SkeletonCards = () => (
  <div class="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 gap-4">
    <For each={[1, 2, 3]}>
      {() => (
        <div class="cart-rec-grid-card animate-pulse rounded-lg border border-foreground/10 bg-background p-4 flex flex-col gap-3">
          <div class="aspect-square rounded-md bg-foreground/5" />
          <div class="h-4 bg-foreground/5 rounded w-3/4 mx-auto" />
          <div class="h-3 bg-foreground/5 rounded w-1/2 mx-auto" />
          <div class="h-4 bg-foreground/5 rounded w-1/3 mx-auto" />
          <div class="h-10 bg-foreground/5 rounded-md w-full" />
        </div>
      )}
    </For>
  </div>
);

const Price = (props: { product: RecommendedProduct }) => (
  <div class="flex items-center justify-center gap-1.5">
    {props.product.compare_at_price && props.product.compare_at_price > props.product.price ? (
      <>
        <s class="text-xs text-foreground/60">{formatMoney(props.product.compare_at_price)}</s>
        <span class="text-sm text-primary font-semibold">{formatMoney(props.product.price)}</span>
      </>
    ) : props.product.price_varies ? (
      <span class="text-sm text-foreground">
        {formatMoney(props.product.price_min)} - {formatMoney(props.product.price_max)}
      </span>
    ) : (
      <span class="text-sm text-foreground">{formatMoney(props.product.price)}</span>
    )}
  </div>
);

const Action = (props: { product: RecommendedProduct; cartUrl: string }) =>
  props.product.variants.length === 1 ? (
    <form action={props.cartUrl} method="post" class="w-full">
      <input type="hidden" name="id" value={String(props.product.variants[0]?.id)} />
      <button
        type="submit"
        aria-label={`Add ${props.product.title} to cart`}
        class="button button--primary w-full uppercase text-xs tracking-wider rounded-lg font-bold"
        innerHTML={`<add-to-cart-action form-state="initial" class="flex items-center justify-center gap-2"><span data-content>Add to cart</span><template data-state="adding"><i class="fa-solid fa-spinner-third motion-preset-spin"></i></template><template data-state="added"><i class="fa-solid fa-check"></i> Added</template><template data-state="error"><i class="fa-solid fa-xmark text-red-600"></i> Error</template></add-to-cart-action>`}
      />
    </form>
  ) : (
    <a
      href={props.product.url}
      aria-label={`View options for ${props.product.title}`}
      class="button button--primary w-full uppercase text-xs font-bold tracking-wider no-underline rounded-lg"
    >
      View options
    </a>
  );

const Card = (props: { product: RecommendedProduct; cartUrl: string }) => (
  <div class="keen-slider__slide cart-rec-grid-card flex flex-col gap-3 rounded-lg border border-foreground/10 bg-background p-4">
    <a href={props.product.url} class="no-underline block relative">
      <div class="rounded-md overflow-hidden bg-background border border-foreground/10 aspect-square relative">
        {props.product.compare_at_price && props.product.compare_at_price > props.product.price && (
          <span class="absolute top-2 right-2 z-10 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
            Sale
          </span>
        )}
        {props.product.featured_image && (
          <img
            src={props.product.featured_image}
            alt={props.product.title}
            class="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>
    </a>
    <div class="flex flex-col gap-2 text-center">
      <a href={props.product.url} class="no-underline">
        <p class="text-sm font-bold line-clamp-2 mb-0 text-foreground">{props.product.title}</p>
      </a>
      <div class="jdgm-widget jdgm-preview-badge flex justify-center" data-id={props.product.id} />
      <Price product={props.product} />
    </div>
    <Action product={props.product} cartUrl={props.cartUrl} />
  </div>
);

const Component: CorrectComponentType<Props> = (props, { element }) => {
  const limit = props.dataLimit || 6;
  const intent = props.dataIntent || 'related';

  const { recommendations, isLoading, hasItems } = useCartRecommendations({
    element,
    baseUrl: props.dataUrl,
    cartUrl: props.dataCartUrl,
    limit,
    intent,
  });

  const sliderId = `cart-recs-grid-${Math.random().toString(36).slice(2, 8)}`;

  // Trigger Judge.me to scan newly inserted badges. Most installs auto-scan,
  // but dispatching the event here makes the integration robust.
  createEffect(() => {
    if (recommendations().length === 0) return;
    queueMicrotask(() => {
      try {
        window.dispatchEvent(new Event('jdgm.refresh'));
      } catch {
        /* no-op */
      }
    });
  });

  return (
    <Show when={hasItems()}>
      <div class="block">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl text-heading font-bold font-body mt-0">{props.dataTitle}</h3>
          <Show when={!isLoading() && recommendations().length > 0}>
            <keen-slider-navigation-arrows
              attr:target={`#${sliderId}`}
              attr:is-hidden="true"
              class="keen-arrows flex gap-2 shrink-0"
            >
              <template prev-arrow>
                <i class="fa-solid fa-chevron-left" />
              </template>
              <template next-arrow>
                <i class="fa-solid fa-chevron-right" />
              </template>
            </keen-slider-navigation-arrows>
          </Show>
        </div>

        <Show when={!isLoading()} fallback={<SkeletonCards />}>
          <keen-slider id={sliderId} attr:config={JSON.stringify(SLIDER_CONFIG)} class="overflow-visible! keen-slider">
            <For each={recommendations()}>{(product) => <Card product={product} cartUrl={props.dataCartUrl} />}</For>
          </keen-slider>
        </Show>
      </div>
    </Show>
  );
};

customShadowlessElement(
  'cart-recommendations-grid',
  {
    dataUrl: '',
    dataCartUrl: '/cart/add',
    dataLimit: 6,
    dataIntent: 'related',
    dataTitle: 'Customers also bought',
  },
  correctElementType(Component),
);
